import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRightIcon, ReceiptIcon } from "lucide-react";
import { formatDate } from "@/shared/lib/date-time";
import type { ExpenseRecordListItemDTO } from "../api/client";
import { formatExpenseAmount } from "../lib/expense-format";
import { ExpenseRecordStatusBadge } from "./ExpenseRecordStatusBadge";

type ExpenseRecentRecordsCardProps = {
  records: ExpenseRecordListItemDTO[];
  isLoading: boolean;
};

export function ExpenseRecentRecordsCard({
  records,
  isLoading,
}: ExpenseRecentRecordsCardProps) {
  const nav = useNavigate();

  return (
    <Card className="gap-0 py-0">
      <CardHeader className="space-y-1 border-b px-6 py-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ReceiptIcon className="size-4 text-muted-foreground" />
              ລາຍການລ່າສຸດ
            </CardTitle>
            <CardDescription>5 ລາຍການຄ່າໃຊ້ຈ່າຍຫຼ້າສຸດ</CardDescription>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={() => nav({ to: "/app/expenses" })}
          >
            ເບິ່ງທັງໝົດ
            <ArrowRightIcon className="ml-1 size-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-0 py-0">
        {isLoading ? (
          <p className="px-6 py-8 text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        ) : records.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
            <ReceiptIcon className="size-8 text-muted-foreground/50" />
            <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີລາຍການ</p>
          </div>
        ) : (
          <ul className="divide-y">
            {records.map((record) => (
              <li key={record.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left transition-colors hover:bg-muted/30"
                  onClick={() =>
                    nav({
                      to: "/app/expenses/$id",
                      params: { id: record.id },
                    })
                  }
                >
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{record.number}</p>
                    <p className="mt-0.5 truncate text-muted-foreground text-xs">
                      {record.payeeName} · {record.categoryName} ·{" "}
                      {formatDate(record.expenseDate)}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    <span className="font-medium text-sm tabular-nums">
                      {formatExpenseAmount(record.amount)}
                    </span>
                    <ExpenseRecordStatusBadge status={record.status} />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
